/**
 * Periodenleiste (D-02, Sketch 023 Variante C `stripCell()`): zeigt alle Monate, Quartale bzw.
 * Jahre eines Auswertungstyps mit dem Status, der sich aus den Daten ableiten laesst -- keine
 * Daten, offen, gesperrte Werte, zurueckrechenbar. Seit Phase 41 (D-08, loest D-23 ein) kommt
 * dazu der Uebertragungsstand: uebertragen / seitdem geaendert / fehlgeschlagen / offen (nie
 * uebertragen); "keine Daten" und "zurueckrechenbar" behalten Vorrang. Der Zustand wird fertig
 * vom Backend geliefert (41-04 Zustandsfunktion) -- keine Ableitung im Frontend.
 *
 * Texte (`label`, `statusLabel`) kommen fertig aus `PeriodStripEntry` (39-08) -- keine eigene
 * Formatierung oder Ableitung hier.
 */
import type { CSSProperties } from 'react';
import { Radio, Text, VisuallyHidden } from '@mantine/core';
import { statusPalette } from '../../theme/colors';
import type {
  PeriodStripEntry,
  PeriodStripStatus,
} from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

const stripColorVars = {
  '--strip-recoverable-color': statusPalette.eingereicht.fg,
  '--strip-transferred-color': statusPalette.freigegeben.fg,
  '--strip-changed-color': statusPalette.eingereicht.fg,
  '--strip-failed-color': statusPalette.fehler.fg,
} as CSSProperties;

const DOT_CLASS: Record<PeriodStripStatus, string> = {
  open: classes.stripDotOpen,
  recoverable: classes.stripDotRecoverable,
  none: classes.stripDotNone,
  transferred: classes.stripDotTransferred,
  changed: classes.stripDotChanged,
  failed: classes.stripDotFailed,
};

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function entryValue(year: number, period: number): string {
  return `${year}-${period}`;
}

export interface PeriodStripProps {
  entries: PeriodStripEntry[];
  selected: { year: number; period: number };
  onSelect: (year: number, period: number) => void;
}

/**
 * Genau ein Zeitraum ist gewaehlt -- deshalb `Radio.Group` mit `Radio.Card`: Kartenoptik aus dem
 * Theme, `role="radio"`/`aria-checked`, Pfeiltasten innerhalb der Gruppe. Zeitraeume ohne Daten
 * sind `disabled`.
 */
export function PeriodStrip({ entries, selected, onSelect }: PeriodStripProps) {
  return (
    <Radio.Group
      value={entryValue(selected.year, selected.period)}
      onChange={(value) => {
        const entry = entries.find(
          (candidate) => entryValue(candidate.year, candidate.period) === value
        );
        if (entry && entry.status !== 'none') {
          onSelect(entry.year, entry.period);
        }
      }}
      label={<VisuallyHidden>Zeiträume</VisuallyHidden>}
      mb="md"
    >
      <div
        className={classes.strip}
        style={{
          ...stripColorVars,
          gridTemplateColumns: `repeat(${entries.length}, minmax(0, 1fr))`,
        }}
      >
        {entries.map((entry) => (
          <Radio.Card
            key={entryValue(entry.year, entry.period)}
            value={entryValue(entry.year, entry.period)}
            data-testid="period-strip-cell"
            data-status={entry.status}
            disabled={entry.status === 'none'}
            withBorder
            radius="md"
            px="sm"
            py="xs"
            className={classes.stripCell}
          >
            <Text fw={700} fz="sm" c="vdzGray.8" truncate>
              {entry.label}
            </Text>
            <Text
              component="span"
              fz="xs"
              c="vdzGray.6"
              className={classes.stripStatus}
            >
              <span className={cx(classes.stripDot, DOT_CLASS[entry.status])} />
              {entry.statusLabel}
            </Text>
          </Radio.Card>
        ))}
      </div>
    </Radio.Group>
  );
}
